/**
 * Competitor dataset behind the /<slug>-alternative/ comparison pages and the
 * /compare-estate-agents/ hub.
 *
 * Kept as plain data (no JSX, no CSS imports) so it can be imported by BOTH the
 * React pages and the build scripts (scripts/prerender.mjs, generate-sitemap.mjs)
 * — one source of truth for the routes that exist.
 *
 * ── Accuracy policy ──────────────────────────────────────────────────────────
 * These are competitors' PUBLISHED prices, checked on `VERIFIED_AT`. Comparative
 * claims have to be accurate and substantiable (CAP Code r.3.33), so:
 *   - quote headline/entry prices only, always hedged ("from", "typically")
 *   - never state a competitor's price as if it were the only price they offer
 *   - render VERIFIED_AT on every page so the reader can date the comparison
 *   - `weaknesses` must be structural facts about the MODEL (a subscription
 *     renews; an upfront fee is payable whether or not you sell), not slurs
 * If you update a price, update VERIFIED_AT in the same commit.
 *
 * Don't list two brands from the same group as separate comparisons — it reads
 * as padding and splits the same intent across two thin pages. Strike was
 * dropped for exactly this reason (same group as Purplebricks since 2023).
 *
 * Optional per-entry field: `heroHeadline` overrides the default
 * "The free alternative to <name>" H1. Use it whenever that framing would be
 * inaccurate — e.g. a competitor whose own entry tier is genuinely free.
 * No current entry needs it; it exists so the honest framing stays cheap.
 */

/** Date the competitor pricing below was last checked against their own sites. */
export const VERIFIED_AT = '2026-07-30'

/** Human-readable version of VERIFIED_AT, e.g. "30 July 2026". */
export const VERIFIED_AT_LABEL = new Date(`${VERIFIED_AT}T00:00:00Z`).toLocaleDateString('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
})

export const COMPETITORS = [
  {
    slug: 'offagent',
    name: 'OffAgent',
    // Search-intent title target: "offagent alternative"
    headlineFee: 'From £99/month',
    heroCost: 'from £99 a month',
    feeDetail: '£99 a month rolling, £247 for 3 months, or £494 for 6 months',
    commission: '0%',
    contract: 'Rolling monthly',
    portals: 'Rightmove + Zoopla',
    model: 'subscription',
    payWhen: 'Monthly, up front, whether or not you sell',
    bestFor: 'Sellers who want a Rightmove listing and will pay monthly for it',
    summary:
      'OffAgent is a private-sale platform: you pay a monthly subscription and they list your property on Rightmove and Zoopla under their own agent membership. There is no commission on completion — but the subscription keeps running for as long as your home is on the market.',
    weaknesses: [
      {
        point: 'The meter runs while your house sits',
        detail:
          'A subscription is priced per month, not per sale. A home that takes six months to sell costs roughly six times what one that sells in a month costs — and the slowest sales are exactly the ones where the seller can least afford it.',
      },
      {
        point: 'You pay before you know if it works',
        detail:
          'The first payment is due at listing, so the cost lands whether you get one enquiry or fifty.',
      },
      {
        point: 'Add-ons sit on top of the headline price',
        detail:
          'Photography, floorplans, EPCs, boards and premium portal placement are priced separately, so the real cost of a sale is usually well above the advertised entry price.',
      },
    ],
    keyzeeEdge:
      'No subscription, no add-on ladder and nothing to cancel — and a slow sale costs exactly the same as a fast one: nothing.',
  },
  {
    slug: 'purplebricks',
    name: 'Purplebricks',
    headlineFee: 'From around £1,499',
    heroCost: 'around £1,499',
    feeDetail: 'a fixed fee in the region of £1,499, payable up front or deferred',
    commission: '0%',
    contract: 'Fixed term',
    portals: 'Rightmove + Zoopla',
    model: 'fixed fee',
    payWhen: 'Up front, or deferred — but payable either way',
    bestFor: 'Sellers who want a named local agent and will pay a fixed fee for one',
    summary:
      'Purplebricks is the best-known hybrid agent: a fixed fee instead of percentage commission, with a local property expert to handle valuation and (optionally) viewings. The fee is owed whether or not the property sells. Purplebricks and Strike have been part of the same group since 2023, so comparing one covers both.',
    weaknesses: [
      {
        point: 'The fee is payable even if you never sell',
        detail:
          'This is the defining risk of the fixed-fee model. Withdraw, or fail to find a buyer, and the fee is still due — deferring payment delays it, it does not cancel it.',
      },
      {
        point: 'Four figures before a single viewing',
        detail:
          'On a £238,000 home, roughly £1,499 is around 0.6% of the sale price committed on day one, with no result attached to it.',
      },
      {
        point: 'You are still working through an intermediary',
        detail:
          'Enquiries and feedback route via the agent, which is the relay a private sale is meant to remove.',
      },
    ],
    keyzeeEdge:
      'There is no downside risk either: if your home does not sell, you have lost nothing but the time it took to write the listing.',
  },
  {
    slug: 'yopa',
    name: 'Yopa',
    headlineFee: 'From around £999',
    heroCost: 'from around £999',
    feeDetail: 'a fixed fee typically between £999 and £1,499 depending on region and package',
    commission: '0%',
    contract: 'Fixed term',
    portals: 'Rightmove + Zoopla',
    model: 'fixed fee',
    payWhen: 'Up front or on completion, depending on the package chosen',
    bestFor: 'Sellers who want a local agent doing valuations and viewings for a fixed price',
    summary:
      'Yopa is a hybrid agent pairing a fixed fee with a local agent who values the property and can run viewings. Pricing varies by region and by whether you take the pay-later option.',
    weaknesses: [
      {
        point: 'Regional pricing makes the headline figure slippery',
        detail:
          'The advertised "from" price is not what most sellers pay; the actual quote depends on where you are and which package you take.',
      },
      {
        point: 'Pay-later is still pay',
        detail:
          'Deferred packages typically cost more overall than paying up front, and the fee falls due on a deadline whether or not the sale has completed.',
      },
      {
        point: 'Four figures for distribution you can partly get for free',
        detail:
          'Much of what the fee buys is portal exposure and a listing page — neither of which needs a four-figure commitment.',
      },
    ],
    keyzeeEdge:
      'The same everywhere in England and Wales — no regional pricing, no package to choose and no deadline attached to a fee.',
  },
  {
    slug: '99home',
    name: '99home',
    headlineFee: 'From around £99',
    heroCost: 'from around £99',
    feeDetail: 'entry packages from around £99, with higher tiers for more support',
    commission: '0%',
    contract: 'Package-based',
    portals: 'Rightmove + Zoopla',
    model: 'tiered packages',
    payWhen: 'Up front, per package',
    bestFor: 'Budget sellers comfortable running the whole sale themselves',
    summary:
      '99home is a low-cost online agent selling tiered listing packages, with Rightmove and Zoopla distribution at the entry level and more hand-holding further up the range.',
    weaknesses: [
      {
        point: 'The £99 tier is deliberately minimal',
        detail:
          'The entry package is distribution and little else; support, photography and viewing management sit in the pricier tiers.',
      },
      {
        point: 'Package pricing is hard to compare',
        detail:
          'Tier boundaries and what falls inside them shift, which makes the true cost of a sale difficult to work out before you commit.',
      },
      {
        point: 'Paid up front, non-contingent',
        detail:
          'You buy the package regardless of outcome — there is no refund attached to failing to sell.',
      },
    ],
    keyzeeEdge:
      'There is one tier, no package to outgrow and no upgrade path priced against you.',
  },
]

/** The traditional high-street baseline every comparison page measures against. */
export const HIGH_STREET = {
  name: 'High-street agent',
  headlineFee: '1%–3% + VAT',
  commission: '1%–3% + VAT',
  contract: 'Sole agency, typically 12–16 weeks',
  portals: 'Rightmove + Zoopla',
  payWhen: 'On completion',
  bestFor: 'Sellers who want the whole thing handled and will pay commission for it',
}

/** Keyzee's own row, so the comparison table has a consistent shape. */
export const KEYZEE_ROW = {
  name: 'Keyzee',
  headlineFee: '£0',
  commission: '0%',
  contract: 'None — cancel any time',
  portals: 'Keyzee, plus list alongside Rightmove yourself',
  payWhen: 'Never — listing and selling are free',
  bestFor: 'Sellers happy to talk to buyers directly and keep every pound',
}

/** Look up one competitor by slug. Returns undefined when unknown. */
export const getCompetitor = (slug) => COMPETITORS.find((c) => c.slug === slug)

/** Route paths for the generated comparison pages (no trailing slash). */
export const COMPARE_ROUTES = [
  '/compare-estate-agents',
  ...COMPETITORS.map((c) => `/${c.slug}-alternative`),
]
